/* WSS Game Zone — los juegos de Kahoot y Wordwall con pestaña propia.
   Aprobado el 28/10/2026: categorías que crea la escuela, dentro de cada una
   primero Kahoot y después Wordwall, y galardones por las veces que juegan.
   La misma pantalla sirve al estudiante y, con los botones de más, al profe. */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Gamepad2, Play, Star, ExternalLink, Plus, Trash2, Pencil, Trophy, Target,
  Sparkles, Flame, Medal, Crown, GraduationCap, FolderPlus, X, Download,
} from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

const FUENTE = {
  kahoot: { label: "Kahoot", chip: "bg-rose-100 text-rose-700", punto: "bg-rose-500" },
  wordwall: { label: "Wordwall", chip: "bg-violet-100 text-violet-700", punto: "bg-violet-500" },
  other: { label: "Juego", chip: "bg-slate-100 text-slate-700", punto: "bg-slate-400" },
};

const AWARD_ICON = { sparkles: Sparkles, gamepad: Gamepad2, flame: Flame, medal: Medal, crown: Crown, target: Target };

const Etiqueta = ({ children, className = "" }) => (
  <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${className}`}>{children}</div>
);

const GameCard = ({ g, onPlay, canManage, onEdit, onDelete }) => {
  const f = FUENTE[g.source] || FUENTE.other;
  return (
    <div className="group relative bg-white border border-slate-200 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 hover:border-slate-300"
         data-testid={`game-card-${g.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${f.chip}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${f.punto}`} /> {f.label}
        </span>
        {g.level && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">{g.level}</span>}
      </div>
      <button onClick={() => onPlay(g)} className="block w-full text-left" data-testid={`game-play-${g.id}`}>
        <div className="mt-4 font-display text-base font-bold tracking-tight text-slate-900 leading-snug">{g.title}</div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-slate-500">{g.plays || 0} partidas</span>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 group-hover:text-brand-orange transition-colors">
            <Play className="h-3.5 w-3.5 fill-current" /> Jugar
          </span>
        </div>
      </button>
      {canManage && (
        <div className="absolute top-3 right-3 hidden group-hover:flex gap-1 bg-white/95 rounded-lg p-0.5 shadow-sm">
          <button onClick={() => onEdit(g)} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100"
                  data-testid={`game-edit-${g.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button>
          <button onClick={() => onDelete(g)} className="h-7 w-7 grid place-items-center rounded hover:bg-rose-50"
                  data-testid={`game-delete-${g.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button>
        </div>
      )}
    </div>
  );
};

export default function GameZone() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const canManage = ["superadmin", "academic_director", "teacher"].includes(user?.role);

  const [data, setData] = useState({ categories: [], featured: [], awards: [], total_plays: 0 });
  const [loading, setLoading] = useState(true);
  const [jugando, setJugando] = useState(null);
  const [marcoFallo, setMarcoFallo] = useState(false);
  const [catForm, setCatForm] = useState(null);     // {id?, name}
  const [gameForm, setGameForm] = useState(null);   // {id?, category_id, title, url, level, featured}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/game-zone/overview");
      setData(d);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const abrir = async (g) => {
    setMarcoFallo(false);
    setJugando(g);
    try {
      const { data: r } = await api.post(`/game-zone/games/${g.id}/play`);
      (r.new_awards || []).forEach((a) => toast.success(`🏆 ${a[lang] || a.es}`));
      load();
    } catch { /* jugar no se bloquea porque falle el conteo */ }
  };

  const guardarCategoria = async () => {
    setBusy(true);
    try {
      if (catForm.id) await api.patch(`/game-zone/categories/${catForm.id}`, { name: catForm.name });
      else await api.post("/game-zone/categories", { name: catForm.name });
      setCatForm(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
    finally { setBusy(false); }
  };

  const borrarCategoria = async (c) => {
    if (!window.confirm(`¿Borrar la categoría "${c.name}"?`)) return;
    try {
      await api.delete(`/game-zone/categories/${c.id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
  };

  const guardarJuego = async () => {
    setBusy(true);
    try {
      const body = {
        category_id: gameForm.category_id, title: gameForm.title,
        url: gameForm.url, level: gameForm.level || "", featured: !!gameForm.featured,
      };
      if (gameForm.id) await api.patch(`/game-zone/games/${gameForm.id}`, body);
      else await api.post("/game-zone/games", body);
      setGameForm(null);
      await load();
      toast.success(gameForm.id ? "Juego actualizado" : "Juego agregado");
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
    finally { setBusy(false); }
  };

  const borrarJuego = async (g) => {
    if (!window.confirm(`¿Borrar "${g.title}"?`)) return;
    await api.delete(`/game-zone/games/${g.id}`);
    await load();
  };

  const importar = async () => {
    setBusy(true);
    try {
      const { data: prev } = await api.post("/game-zone/import-from-resources", null,
                                            { params: { dry_run: true } });
      if (!prev.imported) {
        toast.info(t("game_zone_import_none"));
        return;
      }
      const cats = prev.new_categories.length
        ? `\n\nCategorías nuevas: ${prev.new_categories.join(", ")}` : "";
      if (!window.confirm(`${t("game_zone_import_confirm", { n: prev.imported })}${cats}`)) return;
      const { data: r } = await api.post("/game-zone/import-from-resources", null,
                                         { params: { dry_run: false } });
      toast.success(t("game_zone_import_done", { n: r.imported }));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
    finally { setBusy(false); }
  };

  const ganados = (data.awards || []).filter((a) => a.earned);
  const siguiente = (data.awards || []).find((a) => !a.earned);

  return (
    <div className="max-w-[1200px]" data-testid="game-zone-page">
      {/* portada */}
      <div className="rounded-3xl bg-slate-900 text-white p-8 relative overflow-hidden mb-6">
        <span className="absolute inset-0 opacity-[0.07]"
              style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <span className="absolute -right-10 -top-16 h-56 w-56 rounded-full bg-brand-orange/25 blur-2xl" />
        <div className="relative flex items-start gap-4 flex-wrap">
          <span className="h-14 w-14 rounded-2xl grid place-items-center bg-white/15 ring-1 ring-white/25 shrink-0">
            <Gamepad2 className="h-7 w-7" />
          </span>
          <div className="flex-1 min-w-[260px]">
            {user?.spanish_level && <Etiqueta className="text-white/50">{t("your_level")}: {user.spanish_level}</Etiqueta>}
            <h1 className="mt-1.5 font-display text-3xl sm:text-4xl font-bold tracking-tight">WSS Game Zone</h1>
            <p className="mt-1.5 text-sm text-white/70 max-w-lg">{t("game_zone_sub")}</p>
          </div>
          {canManage && (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="secondary" className="rounded-full"
                      onClick={importar} disabled={busy} data-testid="import-resources-button">
                <Download className="h-3.5 w-3.5" /> {t("game_zone_import")}
              </Button>
              <Button size="sm" variant="secondary" className="rounded-full"
                      onClick={() => setCatForm({ name: "" })} data-testid="add-category-button">
                <FolderPlus className="h-3.5 w-3.5" /> {t("game_zone_new_category")}
              </Button>
              <Button size="sm" className="rounded-full bg-brand-orange hover:bg-orange-600"
                      disabled={!data.categories.length}
                      onClick={() => setGameForm({ category_id: data.categories[0]?.id, title: "", url: "", level: "", featured: false })}
                      data-testid="add-game-button">
                <Plus className="h-3.5 w-3.5" /> {t("game_zone_new_game")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* galardones por uso */}
      {data.total_plays > 0 && (
        <div className="mb-10 bg-white border border-slate-200 rounded-2xl p-5" data-testid="game-zone-awards">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-amber-500" />
            <Etiqueta className="text-slate-500">{t("game_zone_awards")} · {data.total_plays} {t("game_zone_plays")}</Etiqueta>
          </div>
          <div className="flex flex-wrap gap-2">
            {ganados.map((a) => {
              const Icon = AWARD_ICON[a.icon] || Trophy;
              return (
                <span key={a.key} data-testid={`award-${a.key}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                  <Icon className="h-3.5 w-3.5" /> {a[lang] || a.es}
                </span>
              );
            })}
            {siguiente && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 px-3 py-1.5 rounded-full border border-dashed border-slate-300"
                    data-testid="award-next">
                {siguiente[lang] || siguiente.es} · {siguiente.progress}/{siguiente.needed}
              </span>
            )}
          </div>
        </div>
      )}

      {/* destacados */}
      {data.featured?.length > 0 && (
        <div className="mb-10" data-testid="game-zone-featured">
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
            <Etiqueta className="text-slate-500">{t("game_zone_featured")}</Etiqueta>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.featured.slice(0, 3).map((g) => (
              <button key={g.id} onClick={() => abrir(g)} data-testid={`featured-${g.id}`}
                      className="group text-left rounded-2xl p-5 bg-gradient-to-br from-slate-900 to-slate-800 text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/15">
                    {(FUENTE[g.source] || FUENTE.other).label}
                  </span>
                  {g.level && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-brand-orange">{g.level}</span>}
                </div>
                <div className="mt-4 font-display text-lg font-bold tracking-tight leading-snug">{g.title}</div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold">
                  <Play className="h-3.5 w-3.5 fill-current" /> {t("game_zone_play_now")}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* categorías */}
      {!loading && data.categories.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="game-zone-empty">
          <Gamepad2 className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm text-slate-500">{t("game_zone_empty")}</p>
        </div>
      )}

      {data.categories.map((c) => (
        <div key={c.id} className="mb-10" data-testid={`category-${c.id}`}>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="h-10 w-10 rounded-xl grid place-items-center bg-slate-900 text-white">
              <Gamepad2 className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <div className="font-display text-xl font-bold tracking-tight text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500">{c.game_count} {t("game_zone_games")}</div>
            </div>
            {canManage && (
              <div className="flex gap-1">
                <button onClick={() => setGameForm({ category_id: c.id, title: "", url: "", level: "", featured: false })}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"
                        data-testid={`category-add-game-${c.id}`}><Plus className="h-4 w-4 text-slate-500" /></button>
                <button onClick={() => setCatForm({ id: c.id, name: c.name })}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"
                        data-testid={`category-edit-${c.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button>
                <button onClick={() => borrarCategoria(c)}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-rose-50"
                        data-testid={`category-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button>
              </div>
            )}
          </div>
          {c.games.length === 0 ? (
            <p className="text-sm text-slate-400 pl-1">{t("game_zone_category_empty")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {c.games.map((g) => (
                <GameCard key={g.id} g={g} onPlay={abrir} canManage={canManage}
                          onEdit={(x) => setGameForm({ ...x })} onDelete={borrarJuego} />
              ))}
            </div>
          )}
        </div>
      ))}

      {/* jugando */}
      <Dialog open={!!jugando} onOpenChange={(v) => !v && setJugando(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="game-player-dialog">
          <DialogHeader className="px-5 py-3 border-b border-slate-200">
            <DialogTitle className="flex items-center gap-2.5 text-base pr-8">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${(FUENTE[jugando?.source] || FUENTE.other).chip}`}>
                {(FUENTE[jugando?.source] || FUENTE.other).label}
              </span>
              <span className="truncate">{jugando?.title}</span>
              {jugando?.level && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">{jugando.level}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-slate-50">
            {jugando?.embed_url && !marcoFallo ? (
              <iframe title={jugando.title} src={jugando.embed_url} className="h-full w-full border-0"
                      allowFullScreen onError={() => setMarcoFallo(true)} data-testid="game-iframe" />
            ) : (
              <div className="h-full grid place-items-center text-center px-6">
                <div>
                  <Gamepad2 className="h-10 w-10 text-slate-300 mx-auto" />
                  <p className="mt-3 text-sm text-slate-500 max-w-sm">{t("game_zone_open_outside")}</p>
                </div>
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
            <a href={jugando?.url} target="_blank" rel="noreferrer" data-testid="game-open-tab"
               className="text-[13px] font-semibold text-slate-500 inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors">
              <ExternalLink className="h-3.5 w-3.5" /> {t("game_zone_open_tab")}
            </a>
            <Button size="sm" variant="ghost" onClick={() => setJugando(null)} data-testid="game-close">
              <X className="h-3.5 w-3.5" /> {t("close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* categoría */}
      <Dialog open={!!catForm} onOpenChange={(v) => !v && setCatForm(null)}>
        <DialogContent className="max-w-sm" data-testid="category-dialog">
          <DialogHeader><DialogTitle>{catForm?.id ? t("game_zone_edit_category") : t("game_zone_new_category")}</DialogTitle></DialogHeader>
          <div>
            <Label>{t("name")}</Label>
            <Input className="mt-1" value={catForm?.name || ""} autoFocus
                   placeholder="Verbos, Vocabulario, Cultura…"
                   onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                   data-testid="category-name-input" />
          </div>
          <Button onClick={guardarCategoria} disabled={busy || !catForm?.name?.trim()}
                  data-testid="category-save-button">{t("save")}</Button>
        </DialogContent>
      </Dialog>

      {/* juego */}
      <Dialog open={!!gameForm} onOpenChange={(v) => !v && setGameForm(null)}>
        <DialogContent className="max-w-lg" data-testid="game-dialog">
          <DialogHeader><DialogTitle>{gameForm?.id ? t("game_zone_edit_game") : t("game_zone_new_game")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("game_zone_link")}</Label>
              <Input className="mt-1" value={gameForm?.url || ""}
                     placeholder="https://wordwall.net/resource/… o https://kahoot.it/…"
                     onChange={(e) => setGameForm({ ...gameForm, url: e.target.value })}
                     data-testid="game-url-input" />
              <p className="text-[11px] text-slate-500 mt-1.5">{t("game_zone_link_hint")}</p>
            </div>
            <div>
              <Label>{t("title")}</Label>
              <Input className="mt-1" value={gameForm?.title || ""}
                     onChange={(e) => setGameForm({ ...gameForm, title: e.target.value })}
                     data-testid="game-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("category")}</Label>
                <Select value={gameForm?.category_id || ""}
                        onValueChange={(v) => setGameForm({ ...gameForm, category_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="game-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {data.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("level")}</Label>
                <Select value={gameForm?.level || "none"}
                        onValueChange={(v) => setGameForm({ ...gameForm, level: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="game-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("game_zone_any_level")}</SelectItem>
                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-slate-900" checked={!!gameForm?.featured}
                     onChange={(e) => setGameForm({ ...gameForm, featured: e.target.checked })}
                     data-testid="game-featured-check" />
              <Star className="h-3.5 w-3.5 text-amber-500" /> {t("game_zone_feature_it")}
            </label>
            <Button onClick={guardarJuego} disabled={busy || !gameForm?.url?.trim() || !gameForm?.category_id}
                    data-testid="game-save-button">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
